/**
 * SSO Service
 *
 * Pure service module for SSO authentication with no React dependencies.
 * Handles login flow discovery, redirect URL construction, CSRF validation,
 * and loginToken exchange via the Matrix SSO protocol.
 */

import { createClient } from 'matrix-js-sdk';
import logger from '../utils/logger';
import { DEFAULT_HOMESERVER } from '../config/homeserver';
import { AUTH_ERROR_MESSAGES } from '../utils/authErrors';
import { timFetch } from '../utils/timUserAgent';

const CSRF_TTL_MS = 10 * 60 * 1000; // 10 minutes
const CSRF_STORAGE_KEY = 'sso:csrf_state';

/**
 * Fetch SSO login flows from the homeserver
 * @param {string} [homeserverUrl] - Homeserver base URL (default: DEFAULT_HOMESERVER)
 * @returns {Promise<{identityProviders: Array, supportsSSO: boolean}>}
 */
export async function fetchSSOLoginFlows(homeserverUrl = DEFAULT_HOMESERVER) {
  const url = `${homeserverUrl}/_matrix/client/v3/login`;
  logger.debug('ssoService: Fetching login flows from', url);

  const response = await timFetch(url, {
    method: 'GET',
    mode: 'cors',
    credentials: 'omit',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch login flows: ${response.status}`);
  }

  const data = await response.json();
  const ssoFlow = data.flows?.find(f => f.type === 'm.login.sso');

  if (!ssoFlow) {
    return { identityProviders: [], supportsSSO: false };
  }

  const identityProviders = (ssoFlow.identity_providers || []).filter(p => p.id && p.name);

  logger.debug('ssoService: Found SSO flow with providers:', identityProviders.map(p => p.name));

  return { identityProviders, supportsSSO: true };
}

/**
 * Build the SSO redirect URL for a given identity provider
 * Also generates and stores a CSRF nonce in sessionStorage.
 *
 * @param {string} providerId - The identity provider ID (e.g., "oidc-keycloak")
 * @param {Object} [options] - Options
 * @param {string} [options.homeserverUrl] - Homeserver base URL (default: DEFAULT_HOMESERVER)
 * @param {string} [options.loginHint] - Email/username hint passed to the IdP
 * @returns {string} The full SSO redirect URL
 */
export function buildSSORedirectUrl(providerId, options = {}) {
  const { homeserverUrl = DEFAULT_HOMESERVER, loginHint } = options;

  // Generate CSRF nonce
  const nonce = crypto.randomUUID();
  const csrfState = {
    nonce,
    timestamp: Date.now(),
    homeserver: homeserverUrl,
  };
  sessionStorage.setItem(CSRF_STORAGE_KEY, JSON.stringify(csrfState));

  // Build redirectUrl — the URL Synapse will redirect back to after SSO
  // We encode the nonce in the redirectUrl so Synapse echoes it back
  const origin = window.location.origin;
  const pathname = window.location.pathname || '/';
  const redirectUrl = `${origin}${pathname}?sso_nonce=${encodeURIComponent(nonce)}`;

  let ssoUrl =
    `${homeserverUrl}/_matrix/client/v3/login/sso/redirect/` +
    `${encodeURIComponent(providerId)}` +
    `?redirectUrl=${encodeURIComponent(redirectUrl)}`;

  // Append login_hint if provided (standard OIDC parameter, Synapse passes it to the IdP)
  if (loginHint) {
    ssoUrl += `&login_hint=${encodeURIComponent(loginHint)}`;
  }

  logger.debug('ssoService: Built SSO redirect URL for provider:', providerId);

  return ssoUrl;
}

/**
 * Validate the CSRF nonce from the SSO callback and return stored state.
 * One-time use: removes the stored nonce after validation.
 *
 * @param {string} nonce - The nonce from the callback URL
 * @returns {{valid: boolean, homeserver?: string}} Validation result with stored homeserver
 */
export function validateAndConsumeCSRFState(nonce) {
  try {
    const stored = sessionStorage.getItem(CSRF_STORAGE_KEY);
    if (!stored) {
      logger.warn('ssoService: No CSRF state found in sessionStorage');
      return { valid: false };
    }

    const csrfState = JSON.parse(stored);

    // Remove after reading (one-time use)
    sessionStorage.removeItem(CSRF_STORAGE_KEY);

    // Check nonce matches
    if (csrfState.nonce !== nonce) {
      logger.warn('ssoService: CSRF nonce mismatch');
      return { valid: false };
    }

    // Check timestamp is within TTL
    const age = Date.now() - csrfState.timestamp;
    if (age > CSRF_TTL_MS) {
      logger.warn('ssoService: CSRF nonce expired (age:', age, 'ms)');
      return { valid: false };
    }

    logger.debug('ssoService: CSRF nonce validated successfully');
    return { valid: true, homeserver: csrfState.homeserver || DEFAULT_HOMESERVER };
  } catch (error) {
    logger.error('ssoService: CSRF validation error:', error);
    return { valid: false };
  }
}

/**
 * Exchange a loginToken for Matrix credentials
 *
 * @param {string} loginToken - The loginToken from SSO callback
 * @param {string} [homeserverUrl] - Homeserver base URL (default: DEFAULT_HOMESERVER)
 * @returns {Promise<Object>} The full LoginResponse from the Matrix server
 */
export async function exchangeLoginToken(loginToken, homeserverUrl = DEFAULT_HOMESERVER) {
  logger.debug('ssoService: Exchanging loginToken for credentials at', homeserverUrl);

  const tempClient = createClient({
    baseUrl: homeserverUrl,
    fetchFn: timFetch,
  });

  // 30-second timeout to prevent indefinite hangs
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error('Token exchange timed out')), 30000);
  });

  try {
    const loginResponse = await Promise.race([
      tempClient.login('m.login.token', {
        token: loginToken,
        initial_device_display_name: 'TI-M Web',
        refresh_token: true,
      }),
      timeoutPromise,
    ]);

    logger.debug('ssoService: Token exchange successful:', {
      userId: loginResponse.user_id,
      deviceId: loginResponse.device_id,
    });

    return loginResponse;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Open an SSO fallback popup for UIA re-authentication.
 * Used when bootstrapCrossSigning triggers a UIA challenge with m.login.sso flow.
 *
 * The popup loads the Matrix UIA fallback page, which redirects through Keycloak.
 * After the user authenticates, the server marks the UIA session as completed.
 * The caller should then retry makeRequest({ session }) to complete the operation.
 *
 * @param {string} session - The UIA session ID from the 401 response
 * @param {AbortSignal} [signal] - Optional AbortSignal to cancel the popup
 * @param {string} [homeserverUrl] - Homeserver base URL (default: DEFAULT_HOMESERVER)
 * @param {string} [loginHint] - Email/username hint passed to the IdP via login_hint
 * @returns {Promise<void>} Resolves when the popup is closed
 */
export async function openSSOFallbackAuth(session, signal, homeserverUrl = DEFAULT_HOMESERVER, loginHint) {
  let fallbackUrl = `${homeserverUrl}/_matrix/client/v3/auth/m.login.sso/fallback/web?session=${encodeURIComponent(session)}`;
  if (loginHint) {
    fallbackUrl += `&login_hint=${encodeURIComponent(loginHint)}`;
  }
  logger.info('ssoService: Opening SSO fallback auth popup for UIA');

  const popup = window.open(fallbackUrl, 'sso_auth', 'width=600,height=700');
  if (!popup) {
    throw new Error(AUTH_ERROR_MESSAGES.SSO_POPUP_BLOCKED);
  }

  const expectedOrigin = new URL(homeserverUrl).origin;

  return new Promise((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      if (settled) return;
      settled = true;
      clearInterval(checkInterval);
      clearTimeout(timeoutId);
      window.removeEventListener('message', messageHandler);
    };

    // Primary: listen for postMessage('authDone') from the UIA fallback page
    const messageHandler = (event) => {
      if (settled) return;
      logger.debug('ssoService: Received postMessage:', { origin: event.origin, data: event.data, expectedOrigin });
      if (event.origin !== expectedOrigin) return;
      if (event.data === 'authDone') {
        logger.info('ssoService: Received authDone postMessage from UIA fallback — resolving');
        cleanup();
        setTimeout(() => { if (!popup.closed) popup.close(); }, 300);
        resolve();
      }
    };
    window.addEventListener('message', messageHandler);

    // Fallback: poll popup.closed for servers that don't send postMessage
    const checkInterval = setInterval(() => {
      if (settled) return;
      if (signal?.aborted) {
        logger.info('ssoService: SSO popup aborted via signal');
        cleanup();
        popup.close();
        reject(new Error(AUTH_ERROR_MESSAGES.SSO_CANCELLED));
        return;
      }
      if (popup.closed) {
        logger.info('ssoService: SSO popup closed (detected via polling)');
        cleanup();
        resolve();
      }
    }, 500);

    const timeoutId = setTimeout(() => {
      if (settled) return;
      cleanup();
      if (!popup.closed) popup.close();
      reject(new Error(AUTH_ERROR_MESSAGES.SSO_TIMED_OUT));
    }, 5 * 60 * 1000);
  });
}
